import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
// @deprecated F12.1 — Legacy chrome kept only as rollback target when
//             REACT_APP_HEADER_V4_ENABLED=false. HeaderV4+FooterV4 are the
//             default public chrome for guest views (unauth routes).
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";
import V12UserLayout, { useInsideV12UserLayout } from "@/components/user_v12/V12UserLayout";
import { useAuth } from "@/context/AuthContext";

const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";

/**
 * Shared shell for authenticated pages: dark bg + Sidebar + Header.
 * Pages using this layout should just return their content as children.
 *
 * Phase 6B: gracefully handles anonymous visitors on public-read views
 * (marketplace, modules, civilization leaderboard) — rendered through
 * HeaderV4 + FooterV4 (F12.1) so public chrome stays consistent with
 * the landing / pricing surface.
 *
 * Phase VI.5 Wave A · R1 Issue 3+4 · when the current pathname lives
 * under `/app/*` we transparently render the V12 Workspace shell
 * (`V12UserLayout`) instead of the legacy Sidebar+Header pair. This
 * makes every Wave A alias (e.g. /v2/notifications, /v2/marketplace,
 * /v2/content/blog, …) inherit the V12 chrome WITHOUT touching the
 * underlying page components (Zero-Touch Additive).
 */
export default function SidebarLayout({ children }) {
    const { user } = useAuth();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const location = useLocation();
    // MP.7 Hotfix R1 · if parent route already mounts V12UserLayout (via
    // App.js route element wrapper), skip our chrome entirely to avoid
    // double sidebar/topbar. Covers /app/notifications, /marketplace/*,
    // /admin/marketplace/* etc.
    const insideV12 = useInsideV12UserLayout();
    if (insideV12) {
        return <>{children}</>;
    }
    // Wave RF.1 · check sia /app/* sia /v2/* (alias legacy preservato).
    // Wave VII.A R3 · aggiunto /credits/pricing come V12 route · pagina public
    // ma per utenti loggati il chrome V12UserLayout è coerente con il resto.
    // Wave VII.A R4 · esteso con `/pricing` canonical (ex-V13 SSR deprecated).
    const isV12Route = typeof location?.pathname === "string"
        && (
            location.pathname.startsWith("/app/")
            || location.pathname.startsWith("/v2/")
            || location.pathname === "/pricing"
            || location.pathname.startsWith("/pricing/")
            || location.pathname === "/credits/pricing"
            || location.pathname.startsWith("/credits/pricing/")
        );
    // Wave Marketplace V10 · MP.7 · le route /marketplace/* e /admin/marketplace/*
    // ora sono wrappate direttamente in V12UserLayout da App.js · qui diventiamo
    // pass-through per evitare doppio chrome. Legacy `/marketplace` SSR landing
    // non passa da questo componente.
    const isMarketplaceV10 = typeof location?.pathname === "string"
        && (
            /^\/marketplace\/(v10|wishlist|cart|creator|service-bookings|token-redemptions|library|hub)(\/|$)/.test(location.pathname)
            || /^\/admin\/marketplace(\/|$)/.test(location.pathname)
        );
    if (isMarketplaceV10) {
        return <>{children}</>;
    }

    // V12 route · delegate chrome to the achromatic V12 layout.
    // Guests on public V12 pages (if any) fall back to the legacy
    // public chrome below.
    if (isV12Route && user) {
        return <V12UserLayout>{children}</V12UserLayout>;
    }

    if (!user) {
        return (
            <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex flex-col">
                {V4_ENABLED ? <HeaderV4 /> : <Header />}
                <main className="flex-1 min-w-0 px-6 md:px-8 py-6">{children}</main>
                {V4_ENABLED ? <FooterV4 /> : <Footer />}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar
                collapsed={sidebarCollapsed}
                onToggle={() => setSidebarCollapsed((c) => !c)}
            />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main className="flex-1 min-w-0 px-6 md:px-8 py-6">{children}</main>
            </div>
        </div>
    );
}
